import React from 'react';
import { PropTypes as PT } from 'prop-types';
import { FormattedMessage } from 'react-intl';
import { nucleusConstants } from '@lifesize/nucleus';
import _compact from 'lodash/compact';
import _get from 'lodash/get';
import SVG from 'react-inlinesvg';
import callOutgoingIcon from '@lifesize/ux-assets/vectors/svgIcons/call-outgoing.svg';
import callMissedIcon from '@lifesize/ux-assets/vectors/svgIcons/call-missed.svg';
import cancelIcon from '@lifesize/ux-assets/vectors/svgIcons/cancel.svg';
import chatIcon from '@lifesize/ux-assets/vectors/svgIcons/chat.svg';
import audioIcon from '@lifesize/ux-assets/vectors/svgIcons/headset.svg';
import videoIcon from '@lifesize/ux-assets/vectors/svgIcons/video.svg';
import Tooltip from 'components/Tooltip/Tooltip';
import { selectContact } from 'utils/contact-utils';
import classes from './ActivityFeedListItem.module.scss';

const getStatusIcon = (status) => {
  switch (status) {
    case nucleusConstants.CALL_HISTORY_OUTGOING:
      return { icon: callOutgoingIcon };
    case nucleusConstants.CALL_HISTORY_INCOMING:
      return { icon: callOutgoingIcon, class: classes.incoming };
    case nucleusConstants.CALL_HISTORY_MISSED:
      return { icon: callMissedIcon, class: classes.missedCall };
    default:
      return {};
  }
};

function handleStopClick(event) {
  event.stopPropagation();
}

class ActivityFeedListItem extends React.Component {
  videoCallHandler = (event) => {
    event.stopPropagation();
    this.props.handleVideoCall(this.props.call.dialString);
  };

  audioCallHandler = (event) => {
    event.stopPropagation();
    this.props.handleAudioCall(this.props.call.dialString);
  };

  chatHandler = (event) => {
    event.stopPropagation();
    this.props.handleChat(this.props.call.contact);
  };

  deleteSelectedHandler = (event) => {
    event.stopPropagation();
    this.props.handleDeleteSelected(this.props.call.id);
  };

  renderCallStatus = () => {
    const statusIcon = getStatusIcon(this.props.call.callType);
    const iconClass = _compact([
      classes.icon,
      statusIcon.class
    ]).join(' ');

    return (
      <div className={classes.callStatus}>
        <SVG src={statusIcon.icon} className={iconClass} />
      </div>
    );
  };

  renderCallerName = () => {
    const contact = this.props.call.contact;
    const { hideContacts, hideMeetings, hideRooms } = this.props;
    const denySelection = ((_get(contact, 'isUser') || _get(contact, 'isGroupContact') || _get(contact, 'isPersonalContact') || !contact) && hideContacts)
      || (_get(contact, 'isMeeting') && hideMeetings)
      || (_get(contact, 'isRoomSystem') && hideRooms);
    const callerNameClass = _compact([
      classes.callerName,
      this.props.call.callType === nucleusConstants.CALL_HISTORY_MISSED && this.props.call.new ? classes.missedCall : '',
      denySelection && classes.notSelectable
    ]).join(' ');

    return (
      <span
        className={callerNameClass}
        onClick={() => {
          if (denySelection) return;
          if (!contact) {
            if (!hideContacts) {
              this.props.createContact({
                sipUri: this.props.call.dialString,
                name: this.props.call.displayName
              });
            }
            return;
          }
          selectContact(contact, this.props.pushRoute);
        }}
        data-testid="recents-contact"
      >
        {this.props.call.displayName}
      </span>
    );
  };

  renderCallTime = () =>
    (
      <span
        className={
          (this.props.call.callType === nucleusConstants.CALL_HISTORY_MISSED &&
            this.props.call.new) ?
            `${classes.missedCall} ${classes.callTime}` :
            classes.callTime
        }
      >
        {this.props.call.timeElapsed}
      </span>
    );

  renderButtons = () => {
    const icons = [];
    const { call } = this.props;

    const isCallable = !call.contact || _get(call, 'isCallable');
    const isChattable = _get(call, 'isChattable');

    if (isCallable) {
      if (this.props.handleVideoCall) {
        icons.push(
          <button key="1" onClick={this.videoCallHandler} data-testid="recents-video">
            <SVG src={videoIcon} data-testid="videoIcon" />
            <Tooltip label={<FormattedMessage id="contacts.tooltip.video" defaultMessage="Call with camera on" />} />
          </button>
        );
      }
      if (this.props.handleAudioCall) {
        icons.push(
          <button key="2" onClick={this.audioCallHandler} data-testid="recents-audio">
            <SVG src={audioIcon} data-testid="audioIcon" />
            <Tooltip shift="left" label={<FormattedMessage id="contacts.tooltip.audio" defaultMessage="Call with camera off" />} />
          </button>
        );
      }
    } else {
      if (this.props.handleVideoCall) {
        icons.push(
          <button key="1" className={classes.disabled} onClick={handleStopClick} data-testid="recents-video-disabled">
            <SVG src={videoIcon} data-testid="videoIcon" />
          </button>
        );
      }
      if (this.props.handleAudioCall) {
        icons.push(
          <button key="2" className={classes.disabled} onClick={handleStopClick} data-testid="recents-audio-disabled">
            <SVG src={audioIcon} data-testid="audioIcon" />
          </button>
        );
      }
    }

    if (isChattable && this.props.handleChat && this.props.isChatEnabled) {
      icons.push(
        <button key="3" onClick={this.chatHandler} data-testid="recents-chat">
          <SVG src={chatIcon} />
          <Tooltip label={<FormattedMessage id="contacts.tooltip.chat" defaultMessage="Chat" />} />
        </button>
      );
    }

    icons.push(
      <button key="4" onClick={this.deleteSelectedHandler} className={classes.removeButton} data-testid="recents-cancel">
        <SVG src={cancelIcon} data-testid="cancelIcon" />
        <Tooltip shift="left" label={<FormattedMessage id="contacts.tooltip.remove" defaultMessage="Remove" />} />
      </button>
    );

    return icons;
  };

  render() {
    return (
      <li className={classes.recentCallContainer}>
        <div className={classes.callerDetails}>
          {this.renderCallStatus()}
          {this.renderCallerName()}
        </div>
        {this.renderCallTime()}
        <div className={classes.buttons}>
          {this.renderButtons()}
        </div>
      </li>
    );
  }
}

ActivityFeedListItem.propTypes = {
  call: PT.object,
  createContact: PT.func,
  handleAudioCall: PT.func,
  handleChat: PT.func,
  handleDeleteSelected: PT.func.isRequired,
  handleVideoCall: PT.func,
  hideContacts: PT.bool,
  hideMeetings: PT.bool,
  hideRooms: PT.bool,
  isChatEnabled: PT.bool,
  pushRoute: PT.func
};

export default ActivityFeedListItem;
